import type { FC } from 'react';

import { expiryLabel, formatCouponValue, type IHeldCoupon } from '@/components/coupon/held-coupons';

interface ArrivalBubbleProps {
	coupon: IHeldCoupon;
	/** Unclaimed coupons in the pocket, this one included. */
	waiting: number;
	now: number;
	onView: () => void;
	onClose: () => void;
	/** Hover or focus inside the bubble — the caller pauses its auto-hide while engaged. */
	onEngage: (engaged: boolean) => void;
}

/**
 * A floating "new coupon" bubble under the wallet pill. It points at the pocket rather than
 * replacing it: nothing is claimed here, it never takes focus, and it hides itself after a few
 * seconds — the badge stays as the lasting signal.
 */
const ArrivalBubble: FC<ArrivalBubbleProps> = ({ coupon, waiting, now, onView, onClose, onEngage }) => {
	const others = waiting - 1;
	return (
		<div
			className='coupon-bubble'
			role='note'
			aria-label='New coupon'
			onMouseEnter={() => onEngage(true)}
			onMouseLeave={() => onEngage(false)}
			onFocus={() => onEngage(true)}
			onBlur={() => onEngage(false)}
		>
			<span
				className='coupon-bubble__glyph'
				aria-hidden='true'
			>
				<svg
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					strokeLinecap='round'
					strokeLinejoin='round'
				>
					<path d='M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z' />
					<path
						d='M14 6v12'
						strokeDasharray='2 2'
					/>
				</svg>
			</span>

			<div className='coupon-bubble__body'>
				<span className='coupon-bubble__kicker'>New coupon</span>
				<span className='coupon-bubble__value'>
					<b>{formatCouponValue(coupon)}</b> on your next top-up
				</span>
				<span className='coupon-bubble__meta'>
					<span className='coupon-pocket__code'>{coupon.code}</span>
					<span>{expiryLabel(coupon.expiresAt, now)}</span>
				</span>
				{others > 0 && <span className='coupon-bubble__more'>+{others} more waiting</span>}
			</div>

			<div className='coupon-bubble__acts'>
				<button
					type='button'
					className='coupon-pocket__btn coupon-pocket__btn--claim'
					onClick={onView}
				>
					View
				</button>
				<button
					type='button'
					className='coupon-pocket__x'
					aria-label='Hide new coupon notice'
					onClick={onClose}
				>
					<svg
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2.2'
						strokeLinecap='round'
						aria-hidden='true'
					>
						<path d='M6 6l12 12M18 6L6 18' />
					</svg>
				</button>
			</div>
		</div>
	);
};

export default ArrivalBubble;
